import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { ErrorResponse } from '@/api/handlers/responses';
import type { CatalogImportListPage } from '@/api/models';
import { CatalogImportQueryKeys } from './catalogImportQueryKeys';

/** The API's default page of history. */
export const CATALOG_IMPORT_PAGE_SIZE = 20;

/**
 * One page of a branch's import history (`GET catalog-imports`), newest
 * first, undone imports included. `branchId: null` is every branch, which
 * only an OrgAdmin may read (the wizard's Done step groups it by branch).
 *
 * Keeps the current page on screen while the next one loads, as
 * `useBranchDetailsQuery` does. Unwraps the envelope (rulebook §10): a failed
 * read is the query's `error`, code intact (`BRANCH_NOT_ALLOWED`,
 * `BRANCH_NOT_FOUND`).
 */
export const useCatalogImportsQuery = (
    branchId: string | null,
    page: number,
    pageSize: number = CATALOG_IMPORT_PAGE_SIZE,
    enabled = true,
) => {
    const { apiClient } = useApi();

    return useQuery<CatalogImportListPage, ErrorResponse>({
        queryKey: CatalogImportQueryKeys.listPage(branchId, page, pageSize),
        queryFn: async () => {
            const result = await apiClient.catalogImports.getList({
                branchId,
                page,
                pageSize,
            });
            if (!result.success) {
                throw result.error;
            }
            return result.response;
        },
        enabled,
        staleTime: 60_000,
        placeholderData: keepPreviousData,
        // A refusal (403 `BRANCH_NOT_ALLOWED`, 404 `BRANCH_NOT_FOUND`) answers
        // the same every time, and the in-shell page waits for it to show its
        // forbidden notice: only a failure that may pass is tried again.
        retry: (failureCount, error) =>
            !isRefusal(error) && failureCount < RETRIES,
    });
};

const RETRIES = 3;

const isRefusal = (error: ErrorResponse): boolean =>
    error.status >= 400 && error.status < 500;
